import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Folder, Home, Search } from 'lucide-react';

const GROUPS = [
  { id: 'fenster', label: 'Fenster', pattern: /(fenster|rahmen|spannrahmen|dachfenster|drehfenster|plissee-fenster|rollo)/i },
  { id: 'tueren', label: 'Türen', pattern: /(tuer|tür|deur|deuren|drehtuer|pendeltuer|schiebetuer|plisseetuer|schiebe|pendel)/i },
  { id: 'lichtschacht', label: 'Lichtschacht', pattern: /(lichtschacht|gittersafe)/i },
  { id: 'zubehoer', label: 'Zubehör', pattern: /(zubehoer|zubehör|fuss|fuß|gewebe|standardfarben|farbe|pollenschutz|liefer|logo|pdf|test)/i },
  { id: 'sonstiges', label: 'Sonstiges', pattern: null },
];

function groupFor(folder) {
  const haystack = `${folder?.name || ''} ${folder?.path || ''}`;
  return GROUPS.find((group) => group.pattern && group.pattern.test(haystack))?.id || 'sonstiges';
}

function folderSort(a, b) {
  return String(a.name || a.path).localeCompare(String(b.name || b.path), 'de', { sensitivity: 'base' });
}

export default function MediaFolderPane({
  folders = [],
  currentPath = '',
  onOpen,
  className = '',
  showHint = true,
}) {
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const activeRef = useRef(null);

  const grouped = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const buckets = Object.fromEntries(GROUPS.map((group) => [group.id, []]));
    folders
      .filter((folder) => !query || `${folder.name || ''} ${folder.path || ''}`.toLowerCase().includes(query))
      .forEach((folder) => buckets[groupFor(folder)].push(folder));

    return GROUPS
      .map((group) => ({
        ...group,
        folders: buckets[group.id].sort(folderSort),
      }))
      .filter((group) => group.folders.length > 0);
  }, [folders, filter]);

  const visibleFolders = useMemo(() => {
    const out = [];
    grouped.forEach((group) => {
      if (!collapsed[group.id]) out.push(...group.folders);
    });
    return out;
  }, [grouped, collapsed]);

  const navItems = useMemo(() => [{ path: '', name: '/medienpool' }, ...visibleFolders], [visibleFolders]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [currentPath, filter, collapsed]);

  const openPath = (nextPath) => {
    if (typeof onOpen === 'function') onOpen(nextPath);
  };

  const handleKeyDown = (event) => {
    if (event.target?.tagName === 'INPUT' || navItems.length === 0) return;
    const key = event.key;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'].includes(key)) return;
    event.preventDefault();

    const currentIndex = Math.max(0, navItems.findIndex((item) => item.path === currentPath));
    if (key === 'Enter') {
      openPath(navItems[currentIndex]?.path || '');
      return;
    }
    if (key === 'Home') {
      openPath('');
      return;
    }
    if (key === 'End') {
      openPath(navItems[navItems.length - 1]?.path || '');
      return;
    }
    const delta = key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + delta + navItems.length) % navItems.length;
    openPath(navItems[nextIndex]?.path || '');
  };

  return (
    <aside
      className={`min-h-0 flex flex-col focus:outline-none focus:ring-2 focus:ring-[#F26B1A]/30 ${className}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="Medienordner"
    >
      <div className="p-4 border-b border-[#E5E0D2] shrink-0">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold">Ordner</p>
        {showHint && (
          <p className="mt-1 text-[11px] leading-snug text-[#5C5852]">
            Alle Ordner bleiben sichtbar. Pane anklicken, dann mit ↑/↓ wechseln.
          </p>
        )}
        <label className="relative block mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5852]" />
          <input
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Ordner suchen"
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[12.5px]"
          />
        </label>
      </div>

      <div className="p-3 overflow-y-auto space-y-2 min-h-0 flex-1">
        <button
          ref={currentPath === '' ? activeRef : null}
          type="button"
          onClick={() => openPath('')}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-[13px] ${currentPath === '' ? 'bg-[#F26B1A] text-white' : 'hover:bg-[#FAF8F2] text-[#1F1D1A]'}`}
        >
          <Home className="w-4 h-4 shrink-0" />
          <span className="truncate">/medienpool</span>
        </button>

        {grouped.map((group) => {
          const isCollapsed = !!collapsed[group.id];
          return (
            <div key={group.id} className="pt-2 border-t border-[#E5E0D2]/70 first:border-t-0">
              <button
                type="button"
                onClick={() => setCollapsed((next) => ({ ...next, [group.id]: !next[group.id] }))}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-left text-[11px] uppercase tracking-[0.14em] text-[#5C5852] font-semibold hover:bg-[#FAF8F2]"
              >
                <span>{group.label}</span>
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono text-[10px] tracking-normal">{group.folders.length}</span>
                  {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </span>
              </button>
              {!isCollapsed && (
                <div className="mt-1 space-y-0.5">
                  {group.folders.map((folder) => {
                    const active = currentPath === folder.path;
                    return (
                      <button
                        key={folder.path}
                        ref={active ? activeRef : null}
                        type="button"
                        onClick={() => openPath(folder.path)}
                        title={`/medienpool/${folder.path}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-[13px] ${active ? 'bg-[#F26B1A] text-white' : 'hover:bg-[#FAF8F2] text-[#1F1D1A]'}`}
                        style={{ paddingLeft: `${12 + Math.min(folder.depth || 0, 5) * 14}px` }}
                      >
                        <Folder className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-[#F26B1A]'}`} />
                        <span className="truncate">{folder.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {grouped.length === 0 && (
          <p className="px-3 py-6 text-center text-[12.5px] text-[#5C5852] bg-[#FAF8F2] rounded-xl">
            Keine Ordner gefunden.
          </p>
        )}
      </div>

      {showHint && (
        <div className="px-4 py-3 border-t border-[#E5E0D2] text-[11px] text-[#5C5852] shrink-0">
          ↑/↓ Ordner wechseln · Home Root · Enter öffnen
        </div>
      )}
    </aside>
  );
}
